// Minimal line icons for the admin sidebar (v0.14 corporate-design pass).
// Hand-drawn from basic primitives rather than an icon-font/library import --
// keeps the bundle small and every icon visually consistent (same stroke
// width, same 20x20 grid), matching this project's no-framework approach.
import type { SVGProps } from 'react';

function Icon({ children, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export function CalendarIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="14" height="13" rx="1.5" />
      <line x1="3" y1="8" x2="17" y2="8" />
      <line x1="7" y1="2.5" x2="7" y2="5.5" />
      <line x1="13" y1="2.5" x2="13" y2="5.5" />
    </Icon>
  );
}

export function TabletIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="5" y="2" width="10" height="16" rx="1.5" />
      <circle cx="10" cy="15" r="0.6" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/**
 * 2026-10-21: Kiosk-Zugänge -- eine Person neben einem Tablet. Eigenes Symbol, weil es im Menü
 * direkt unter „Kiosk-Einrichtung" steht und zwei gleiche untereinander verschwimmen (23.09.).
 */
export function TabletUserIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="6" cy="7" r="2.4" />
      <path d="M2 17c0 -2.9 1.8 -4.8 4 -4.8s4 1.9 4 4.8" />
      <rect x="12" y="4" width="6" height="12" rx="1" />
      <circle cx="15" cy="13.6" r="0.5" fill="currentColor" stroke="none" />
    </Icon>
  );
}

export function UsersIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="7.5" cy="7" r="2.6" />
      <path d="M2.5 17c0 -3 2.2 -5 5 -5s5 2 5 5" />
      <circle cx="14.5" cy="7.5" r="2" />
      <path d="M12.7 12.3c2.3 0.2 4.3 2 4.3 4.7" />
    </Icon>
  );
}

export function BarChartIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <line x1="3" y1="17" x2="17" y2="17" />
      <rect x="4.5" y="11" width="3" height="6" />
      <rect x="8.5" y="7" width="3" height="10" />
      <rect x="12.5" y="3" width="3" height="14" />
    </Icon>
  );
}

export function TrendingUpIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <polyline points="3,15 8,9 11.5,12 17,5" />
      <polyline points="12.5,5 17,5 17,9.5" />
    </Icon>
  );
}

export function BuildingIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="2.5" width="11" height="15" rx="1" />
      <line x1="7.5" y1="6" x2="7.5" y2="6" />
      <line x1="7" y1="6" x2="8" y2="6" />
      <line x1="12" y1="6" x2="13" y2="6" />
      <line x1="7" y1="9.5" x2="8" y2="9.5" />
      <line x1="12" y1="9.5" x2="13" y2="9.5" />
      <line x1="7" y1="13" x2="8" y2="13" />
      <line x1="12" y1="13" x2="13" y2="13" />
      <line x1="9" y1="17.5" x2="9" y2="14.5" />
      <line x1="11" y1="17.5" x2="11" y2="14.5" />
    </Icon>
  );
}

export function BoxIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <polyline points="3,6.5 10,3 17,6.5 10,10 3,6.5" />
      <line x1="3" y1="6.5" x2="3" y2="14" />
      <line x1="17" y1="6.5" x2="17" y2="14" />
      <polyline points="3,14 10,17.5 17,14" />
      <line x1="10" y1="10" x2="10" y2="17.5" />
    </Icon>
  );
}

/**
 * v0.16: Preisschild für die Einkaufspreise. Bewusst NICHT dasselbe Symbol wie „Artikel"
 * (BoxIcon), obwohl beide Seiten denselben Katalog betreffen -- zwei gleiche Symbole
 * untereinander lassen die Punkte verschwimmen, und genau diese beiden stehen jetzt
 * nebeneinander.
 */
export function TagIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M9.5 3 H16 a1 1 0 0 1 1 1 v6.5 l-7 7 -7.5 -7.5 Z" />
      <circle cx="13.5" cy="6.5" r="1.2" />
    </Icon>
  );
}

/**
 * v0.16: das Monatsblatt einer Abteilung. Ein Beleg mit gezacktem Abriss -- bewusst weder Diagramm
 * (BarChart/TrendingUp stehen direkt darüber) noch Preisschild (TagIcon, Einkaufspreise): Das
 * Blatt ist eine Rechnung, keine Auswertung und keine Preispflege.
 */
export function ReceiptIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M4 3 h12 v14 l-2 -1.2 -2 1.2 -2 -1.2 -2 1.2 -2 -1.2 -2 1.2 Z" />
      <line x1="7" y1="7" x2="13" y2="7" />
      <line x1="7" y1="10.5" x2="13" y2="10.5" />
    </Icon>
  );
}

/**
 * 2026-10-19: die Belege (Rechnung, Umbuchung). Ein Blatt mit Eselsohr -- ein Schriftstück, das
 * hinausgeht, im Unterschied zum gezackten Kassenbon des Monatsblatts direkt darüber.
 */
export function FileTextIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M5 2.5 h7 l3.5 3.5 v11.5 h-10.5 Z" />
      <path d="M12 2.5 v3.5 h3.5" />
      <line x1="7.5" y1="10" x2="13" y2="10" />
      <line x1="7.5" y1="13" x2="13" y2="13" />
    </Icon>
  );
}

export function ShieldUserIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="6.5" cy="6.5" r="2.4" />
      <path d="M2.5 16c0 -2.8 1.9 -4.7 4 -4.7s4 1.9 4 4.7" />
      <path d="M12.5 4 L16.5 5.4 V9 c0 3 -1.8 5 -4 5.8 c-2.2 -0.8 -4 -2.8 -4 -5.8 V5.4 Z" />
    </Icon>
  );
}

export function KeyIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="6" cy="13" r="3" />
      <line x1="8.8" y1="10.8" x2="17" y2="2.6" />
      <line x1="12.5" y1="7.1" x2="14.6" y2="9.2" />
      <line x1="15" y1="4.6" x2="17" y2="6.6" />
    </Icon>
  );
}

export function TrashIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <line x1="3.5" y1="5.5" x2="16.5" y2="5.5" />
      <rect x="5.5" y="5.5" width="9" height="11.5" rx="1" />
      <rect x="7.5" y="2.5" width="5" height="2" />
      <line x1="8.5" y1="8.5" x2="8.5" y2="14" />
      <line x1="11.5" y1="8.5" x2="11.5" y2="14" />
    </Icon>
  );
}

/** 2026-10-21: „Link kopieren" -- zwei verschränkte Kettenglieder. */
export function LinkIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M8.5 11.5 a3 3 0 0 0 4.2 0 l2.6 -2.6 a3 3 0 0 0 -4.2 -4.2 l-1.1 1.1" />
      <path d="M11.5 8.5 a3 3 0 0 0 -4.2 0 l-2.6 2.6 a3 3 0 0 0 4.2 4.2 l1.1 -1.1" />
    </Icon>
  );
}

/** 2026-10-21: Neuigkeiten -- ein großer und ein kleiner Funkelstern. */
export function SparkleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M8 2.5 L9.6 7.4 L14.5 9 L9.6 10.6 L8 15.5 L6.4 10.6 L1.5 9 L6.4 7.4 Z" />
      <path d="M15 11.5 L15.8 13.7 L18 14.5 L15.8 15.3 L15 17.5 L14.2 15.3 L12 14.5 L14.2 13.7 Z" />
    </Icon>
  );
}

export function BookIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M3 4.5 C5 3.3 7.5 3.3 9.5 4.5 V16 C7.5 14.8 5 14.8 3 16 Z" />
      <path d="M17 4.5 C15 3.3 12.5 3.3 10.5 4.5 V16 C12.5 14.8 15 14.8 17 16 Z" />
    </Icon>
  );
}

/**
 * Das Admin-Handbuch (2026-09-27): ein aufgeschlagenes Buch mit einem Schild darauf.
 *
 * Eigenes Symbol, nicht dasselbe `BookIcon` wie beim Abteilungsleiter-Handbuch: Die beiden
 * Einträge stehen direkt untereinander, und zwei gleiche Symbole verschwimmen dort zu einem
 * (dieselbe Lehre wie beim Menüpunkt „Einkaufspreise" am 23.09.). Das Schild ist zugleich das
 * Zeichen, das in diesem Menü ohnehin für „Rolle" steht.
 */
export function BookShieldIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M3 4.5 C5 3.3 7.5 3.3 9.5 4.5 V16 C7.5 14.8 5 14.8 3 16 Z" />
      <path d="M17 4.5 C15 3.3 12.5 3.3 10.5 4.5 V9" />
      <path d="M10.5 16 V4.5 C11.4 3.96 12.4 3.65 13.4 3.56" />
      <path d="M14.2 10 L17.4 11.2 V13.4 C17.4 15 16 16.2 14.2 16.9 C12.4 16.2 11 15 11 13.4 V11.2 Z" />
    </Icon>
  );
}

// v0.14 follow-up, round 5: the sidebar toggle used to be a <button> made of
// three flex-column <span> bars -- structurally unlike every other button in
// the app (which just hold text). Reported unresponsive to real taps on
// Android Chrome specifically, while sibling text buttons in the same header
// worked fine; a plain SVG icon inside the button, the same pattern every
// other icon-button in this app already uses successfully, replaces it.
export function MenuIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <line x1="3" y1="5.5" x2="17" y2="5.5" />
      <line x1="3" y1="10" x2="17" y2="10" />
      <line x1="3" y1="14.5" x2="17" y2="14.5" />
    </Icon>
  );
}

/**
 * Die Lieferungen der Brauerei (2026-09-29): ein Lastwagen.
 *
 * Eigenes Symbol und nicht noch einmal `BoxIcon` wie bei der Inventur: Die beiden Eintraege
 * stehen direkt untereinander, und zwei gleiche Symbole verschwimmen dort zu einem (dieselbe
 * Lehre wie beim Menuepunkt "Einkaufspreise" am 23.09.).
 */
export function TruckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M2 5.5 H11 V13 H2 Z" />
      <path d="M11 8 H14.5 L17.5 10.5 V13 H11 Z" />
      <circle cx="5.5" cy="15" r="1.6" />
      <circle cx="14" cy="15" r="1.6" />
    </Icon>
  );
}

/**
 * 2026-09-30: Zeilenaktionen. „Bearbeiten" und „Löschen" standen auf fünf Pflegeseiten als
 * identischer Text in jeder Zeile -- bei 191 Mitgliedern in Prod also hunderte Male dasselbe
 * Wort. Als Symbol sind sie kürzer und trotzdem eindeutig; alles Seltenere bleibt bewusst Text
 * (zwei gleiche Symbole untereinander verschwimmen, Lehre vom 23.09. -- sieben nebeneinander
 * wären schlimmer als sieben Wörter).
 */
export function PencilIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M13.5 3.5l3 3L7 16H4v-3z" />
      <line x1="11.5" y1="5.5" x2="14.5" y2="8.5" />
    </Icon>
  );
}

/** Speichern. Die Diskette ist das eine Sinnbild, das hier jeder ohne Erklärung liest. */
export function SaveIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M4.5 3.5h8.5l3 3v10a1 1 0 0 1-1 1h-10.5a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1z" />
      <path d="M7 3.5v4h5.5v-4" />
      <rect x="6.5" y="11" width="7" height="5.5" />
    </Icon>
  );
}

/**
 * „Rezeptur" eines Mischgetränks (Owner-Entscheidung 2026-09-30: Zauberstab).
 *
 * Zwei andere Entwürfe wurden VERWORFEN, nachdem ich sie in echter Größe angesehen hatte: ein
 * Glas mit zwei Zuflüssen sah bei 96 px aus wie ein Papierkorb mit Antennen, und ein Kochlöffel
 * mit offener Laffe las sich als LUPE -- in einer Anwendung voller Filter- und Suchbedienelemente
 * wäre das aktiv irreführend gewesen. **Ein Symbol, das man nicht angesehen hat, ist geraten.**
 *
 * Der Zauberstab passt auch inhaltlich: Der Einkaufspreis eines Mischgetränks wird aus der
 * Rezeptur GERECHNET statt getippt.
 */
export function WandIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M4 16 12.5 7.5" />
      <path d="M10.8 5.8 14.2 9.2" />
      <path d="M16 2.5v3M14.5 4h3" />
      <path d="M17 9v2.4M15.8 10.2h2.4" />
      <circle cx="11.5" cy="13" r="0.8" fill="currentColor" stroke="none" />
    </Icon>
  );
}

/** Abbrechen/Schließen. Das Kreuz ist dafür so eingeführt wie die Diskette fürs Speichern. */
export function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M5.5 5.5 14.5 14.5" />
      <path d="M14.5 5.5 5.5 14.5" />
    </Icon>
  );
}

/**
 * 2026-10-06: Einzelbuchungen durchsuchen. Eine Liste mit einer Lupe darüber.
 *
 * Die Lupe ist hier keine Verwechslungsgefahr, sondern die Aussage: Diese Seite SUCHT. (Am 30.09.
 * wurde ein Kochlöffel gerade deshalb verworfen, weil er wie eine Lupe aussah und nichts suchte --
 * umgekehrt gilt es genauso.)
 */
export function ListSearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <line x1="3" y1="4.5" x2="15" y2="4.5" />
      <line x1="3" y1="9" x2="10" y2="9" />
      <line x1="3" y1="13.5" x2="8" y2="13.5" />
      <circle cx="13.5" cy="13" r="3.5" />
      <line x1="16" y1="15.5" x2="18" y2="17.5" />
    </Icon>
  );
}

/**
 * 2026-10-08 (Owner): „Ersetzen von Funktions-Buttons durch Icons, z. B. Zahlungen durch
 * EUR-Icon." Dieselbe Regel wie am 30.09.: Symbol STATT Wort nur bei Aktionen, die sich je Zeile
 * wiederholen, und nur Sinnbilder, die man ohne Tooltip versteht -- am Tablet gibt es keinen.
 */

/** Zahlungen eines Events. Das Euro-Zeichen: ein nach rechts offener Bogen, zwei Querstriche. */
export function EuroIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M15 5.5A6 6 0 1 0 15 14.5" />
      <line x1="3.5" y1="8.5" x2="11.5" y2="8.5" />
      <line x1="3.5" y1="11.5" x2="11.5" y2="11.5" />
    </Icon>
  );
}

/** Event schließen: ein geschlossenes Vorhängeschloss. */
export function LockIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="8.5" width="13" height="9.5" rx="1.5" />
      <path d="M6.5 8.5V6a3.5 3.5 0 0 1 7 0v2.5" />
      <line x1="10" y1="12" x2="10" y2="14.5" />
    </Icon>
  );
}

/**
 * Event wieder öffnen: dasselbe Schloss, der Bügel rechts ausgehängt. Bewusst dasselbe Bild wie
 * beim Schließen -- die beiden erscheinen nie in derselben Zeile, und das Paar erklärt sich so
 * gegenseitig.
 */
export function LockOpenIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="8.5" width="13" height="9.5" rx="1.5" />
      <path d="M6.5 8.5V6a3.5 3.5 0 0 1 7 0" />
      <line x1="10" y1="12" x2="10" y2="14.5" />
    </Icon>
  );
}

/** „Voll bezahlt": ein Haken im Kreis -- erledigt, nicht bloß gespeichert (das ist die Diskette). */
export function CheckCircleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="10" r="7" />
      <path d="M6.8 10.2l2.2 2.2 4.2-4.6" />
    </Icon>
  );
}

/**
 * Zurücksetzen: ein Pfeil gegen den Uhrzeigersinn. Steht für zwei verwandte Handlungen auf
 * verschiedenen Seiten -- den erfassten Zahlbetrag löschen (rot) und einen Abteilungspreis auf den
 * Standardpreis zurückstellen. Beide nehmen eine Eingabe zurück; sie begegnen sich nie auf
 * derselben Seite, verschwimmen also nicht (Lehre vom 23.09.).
 */
export function ResetIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M3.25 10a6.75 6.75 0 1 0 6.75-6.75 7.3 7.3 0 0 0-5.05 2.05L3.25 7" />
      <path d="M3.25 3.25V7H7" />
    </Icon>
  );
}

/**
 * Passwort anzeigen. Ersetzt das Emoji 👁, das auf Windows, iPhone und Android jeweils anders
 * aussah und zu keinem anderen Symbol der Anwendung passte.
 */
export function EyeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M2 10s3-5.5 8-5.5 8 5.5 8 5.5-3 5.5-8 5.5S2 10 2 10z" />
      <circle cx="10" cy="10" r="2.5" />
    </Icon>
  );
}

/** Passwort verbergen: dasselbe Auge, durchgestrichen (ersetzt 🙈). */
export function EyeOffIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M2 10s3-5.5 8-5.5 8 5.5 8 5.5-3 5.5-8 5.5S2 10 2 10z" />
      <circle cx="10" cy="10" r="2.5" />
      <line x1="3.5" y1="3.5" x2="16.5" y2="16.5" />
    </Icon>
  );
}

/**
 * 2026-10-08, Vorschläge 6, 7, 10 (Owner-Freigabe nach Ansicht am echten Stylesheet).
 */

/** CSV herunterladen: ein Pfeil in eine Ablage. Steht immer NEBEN dem Wort „CSV". */
export function DownloadIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M10 3.5v9" />
      <path d="M6.5 9 10 12.5 13.5 9" />
      <path d="M4 14.5V16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1.5" />
    </Icon>
  );
}

/** Drucken. Steht immer NEBEN dem Wort. */
export function PrinterIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M6 7V3.5h8V7" />
      <rect x="3" y="7" width="14" height="7" rx="1.5" />
      <path d="M6 11.5h8v5H6z" />
    </Icon>
  );
}

/**
 * Spaltenfilter im Tabellenkopf, statt „▾" / „▾●". Gefüllt heißt: ein Filter ist aktiv. Die
 * Füllung trägt die Aussage nicht allein -- der Knopf ist dann zusätzlich gelb hinterlegt
 * (FR-137a), und sein zugänglicher Name endet auf „(aktiv)".
 */
export function FilterIcon({ filled, ...props }: SVGProps<SVGSVGElement> & { filled?: boolean }) {
  return (
    <Icon {...props}>
      <path d="M3 4h14l-5.5 6.5V16l-3 1.5v-7z" fill={filled ? 'currentColor' : 'none'} />
    </Icon>
  );
}

/**
 * Korrekturbuchung am Kiosk: ein Minus im Kreis, denn sie bucht NEGATIV. Bis heute trug dieser
 * Knopf dasselbe Zeichen ↩ wie „Anderes Mitglied" -- zwei völlig verschiedene Handlungen, ein Bild.
 */
export function MinusCircleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="10" r="7" />
      <path d="M6.5 10h7" />
    </Icon>
  );
}

export function PlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <line x1="10" y1="4" x2="10" y2="16" />
      <line x1="4" y1="10" x2="16" y2="10" />
    </Icon>
  );
}

/** Zurück zur Übersicht: ein Pfeil nach links (Detailseite eines Events). */
export function ArrowLeftIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <line x1="16" y1="10" x2="4" y2="10" />
      <polyline points="9,5 4,10 9,15" />
    </Icon>
  );
}

/** Ein Hinweis, der sich auf Antippen öffnet: ein „i" im Kreis. */
export function InfoIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="10" r="7" />
      <line x1="10" y1="9" x2="10" y2="14" />
      <line x1="10" y1="6.2" x2="10" y2="6.4" />
    </Icon>
  );
}

/**
 * Ein gebogener Pfeil nach hinten: „kommt von früher" -- ein übernommener oder gerechneter Preis
 * (Einkaufspreise). Bewusst NICHT `ResetIcon`: Das heißt „zurücksetzen" und ist ein Knopf.
 */
export function ArrowBackUpIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <polyline points="8,4 4,8 8,12" />
      <path d="M4 8h8a4 4 0 0 1 0 8h-1" />
    </Icon>
  );
}

/**
 * 2026-10-14 (v0.17): Kartenzahlung -- eine Karte mit Kontaktlos-Wellen. Steht im Menü direkt unter
 * dem Tablet (Kiosk-Einrichtung). Der erste Entwurf (Karte, die oben aus einem Gerät ragt) sah in
 * echter Größe aus wie ein DRUCKER -- und den gibt es schon am Knopf „Drucken". Angesehen, verworfen.
 */
export function CardTerminalIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="2" y="5.5" width="11.5" height="9" rx="1.4" />
      <line x1="2" y1="8.5" x2="13.5" y2="8.5" />
      <line x1="4.5" y1="12" x2="7" y2="12" />
      <path d="M15.5 7.5a3.5 3.5 0 0 1 0 5" />
      <path d="M17.5 5.8a6 6 0 0 1 0 8.4" />
    </Icon>
  );
}

/**
 * 2026-10-18 (v0.17, Owner-Wahl B1): Barzahlung am Kiosk -- ein Geldschein. Steht immer VOR dem
 * Wort „Bar bezahlt", nie allein: Der Knopf sagt auch „neuer Gast", und das kann kein Bild.
 * Verworfen: Schein mit Euro-Münze (in Knopfgröße zu kleinteilig) und das €-Symbol (bedeutet im
 * Admin-Bereich „Zahlungen", und gezahlt wird mit Karte genauso in Euro).
 */
export function BanknoteIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="2" y="5.5" width="16" height="9" rx="1.4" />
      <circle cx="10" cy="10" r="2.2" />
      <line x1="4.6" y1="8" x2="4.6" y2="8" />
      <line x1="15.4" y1="12" x2="15.4" y2="12" />
    </Icon>
  );
}
